import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParticipants } from '../../lib/ParticipantsContext';
import { useSettings } from '../../lib/SettingsContext';
import { useSpinner } from '../../lib/SpinnerContext';
import { useLanguage } from '../../lib/LanguageContext';
import { MailIcon, CopyIcon, Share2Icon } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'react-hot-toast';
import { Modal } from '../ui/modal';
import { useAuth } from '../../context/AuthContext';
import { spinnerService } from '../../services/spinnerService';
import { ShareLoginPromptModal } from './ShareLoginPromptModal';

// Inline SVG icon components
const WhatsappSVG = () => (
  <svg width="22" height="22" viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#25D366"/><path d="M23.472 19.339c-.355-.177-2.104-1.037-2.43-1.155-.326-.119-.563-.177-.8.177-.237.355-.914 1.155-1.122 1.392-.208.237-.415.266-.77.089-.355-.178-1.5-.553-2.86-1.763-1.057-.943-1.77-2.104-1.98-2.459-.208-.355-.022-.546.156-.723.16-.159.355-.414.533-.622.178-.208.237-.355.355-.592.119-.237.06-.444-.03-.622-.089-.178-.8-1.922-1.096-2.633-.289-.693-.583-.599-.8-.61-.208-.009-.444-.011-.68-.011-.237 0-.622.089-.948.444-.326.355-1.24 1.211-1.24 2.955 0 1.744 1.27 3.428 1.448 3.666.178.237 2.5 3.82 6.063 5.207.849.292 1.51.466 2.027.596.851.204 1.627.175 2.24.106.683-.077 2.104-.859 2.403-1.69.296-.83.296-1.541.207-1.69-.089-.148-.326-.237-.681-.414z" fill="#fff"/></svg>
);
const FacebookSVG = () => (
  <svg width="22" height="22" viewBox="0 0 32 32" fill="none"><rect width="32" height="32" rx="8" fill="#1877F2"/><path d="M20.8 16.8h-2.4v8h-3.2v-8h-2.4v-2.4h2.4v-1.6c0-2.4.8-4 3.2-4h2.4v2.4h-1.6c-1.2 0-1.6.4-1.6 1.6v1.6h2.4l-.4 2.4z" fill="#fff"/></svg>
);
const TwitterSVG = () => (
  <svg width="22" height="22" viewBox="0 0 32 32" fill="none"><rect width="32" height="32" rx="8" fill="#000000"/><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="#fff"/></svg>
);
const InstagramSVG = () => (
  <svg width="22" height="22" viewBox="0 0 32 32" fill="none"><rect width="32" height="32" rx="8" fill="#E4405F"/><path d="M16 10.667A5.333 5.333 0 1 0 16 21.333a5.333 5.333 0 0 0 0-10.666zm0 8.667a3.333 3.333 0 1 1 0-6.667 3.333 3.333 0 0 1 0 6.667zm6.4-8.8a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0z" fill="#fff"/><rect x="6.667" y="6.667" width="18.667" height="18.667" rx="5.333" stroke="#fff" strokeWidth="2"/></svg>
);

const TelegramSVG = () => (
  <svg width="22" height="22" viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#0088cc"/><path d="M23.333 10.667l-2.666 12c-.2.867-.7 1.067-1.433.667l-4-2.967-1.933 1.867c-.213.213-.393.393-.8.393l.28-3.933 7.167-6.467c.313-.28-.067-.44-.487-.16l-8.867 5.6-3.8-1.187c-.827-.26-.84-.827.173-1.227l14.8-5.707c.68-.253 1.28.16 1.053 1.12z" fill="#fff"/></svg>
);
const TikTokSVG = () => (
  <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
    <rect width="32" height="32" rx="8" fill="#000000"/>
    <image href="/images/tiktok.png" x="4" y="4" width="24" height="24" style={{ filter: 'brightness(0) invert(1)' }} />
  </svg>
);

export const ShareModal: React.FC<{ isOpen: boolean; onClose: () => void; sidebarSettings: any; spinnerName: string; setSpinnerName: (name: string) => void; }> = ({ isOpen, onClose, sidebarSettings, spinnerName: parentSpinnerName, setSpinnerName }) => {
  const { t } = useTranslation();
  const { participants } = useParticipants();
  const { audioEnabled } = useSettings();
  const { selectedSpinner } = useSpinner();
  const { currentLanguage } = useLanguage();
  const { user } = useAuth();
  const [shareUrl, setShareUrl] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [spinnerName, setLocalSpinnerName] = useState(parentSpinnerName || '');
  const [shareId, setShareId] = useState<string | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [lastSavedName, setLastSavedName] = useState('');
  const [nameChanged, setNameChanged] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [allowAnonymousShare, setAllowAnonymousShare] = useState(true);
  const pendingAction = useRef<null | (() => void)>(null);

  useEffect(() => {
    if (isOpen) setLocalSpinnerName(parentSpinnerName || '');
  }, [isOpen, parentSpinnerName]);

  const shareTemplate = (link: string, spinnerName: string) => {
    const baseText = t('share.shareTemplate', { link });
    const appName = 'Spinner Games !!';
    const enhancedText = spinnerName 
      ? `${spinnerName} - ${appName} 🎯 ${baseText}`
      : `${appName} 🎯 ${baseText}`;
    return enhancedText;
  };

  useEffect(() => {
    setShowLinkInput(false);
    if (shareUrl) setNameChanged(true);
  }, [spinnerName]);

  useEffect(() => {
    if (user && showLoginPrompt && pendingAction.current) {
      setShowLoginPrompt(false);
      pendingAction.current();
      pendingAction.current = null;
    }
    if (user && showLoginPrompt) {
      setShowLoginPrompt(false);
    }
  }, [user, showLoginPrompt]);

  useEffect(() => {
    if (isOpen && !user && !allowAnonymousShare) {
      setShowLoginPrompt(true);
    }
  }, [isOpen, user, allowAnonymousShare]);

  const createOrUpdateShareLink = async () => {
    if (isSharing) return shareId;
    setIsSharing(true);
    try {
      let spinnerId = shareId;
      if (!spinnerId) {
        const payload = {
          participants: sidebarSettings?.participants || [],
          style: selectedSpinner.id,
          language: currentLanguage,
          theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
          sound: audioEnabled,
          spinTime: 5000,
          createdAt: new Date(),
          name: spinnerName || 'Untitled Spinner',
        };
        console.log('DEBUG: ShareModal spinnerName:', spinnerName);
        console.log('DEBUG: ShareModal payload:', payload);
        console.log('DEBUG: ShareModal allowAnonymousShare:', allowAnonymousShare);
        console.log('DEBUG: ShareModal user:', !!user);
        
        // Both authenticated and anonymous users can use Firestore now
        if (user || allowAnonymousShare) {
          try {
            // Create a share link (not a copy) - this will be the original spinner
            spinnerId = await spinnerService.createShareLink(payload as any);
            setShareId(spinnerId);
            setLastSavedName(spinnerName);
          } catch (firestoreError) {
            console.error('Firestore error during sharing:', firestoreError);
            toast.error(t('share.error'));
            return null;
          }
        } else {
          console.error('Cannot share spinner: no user and anonymous sharing not allowed');
          toast.error(t('share.error'));
          return null;
        }
      } else if (spinnerName !== lastSavedName) {
        // Handle name updates for both authenticated and anonymous users
        try {
          await spinnerService.updateSpinnerName(spinnerId, spinnerName || 'Untitled Spinner');
          setLastSavedName(spinnerName);
        } catch (firestoreError) {
          console.error('Firestore error during name update:', firestoreError);
          toast.error(t('share.error'));
          return null;
        }
      }
      
      if (spinnerId) {
        setShareUrl(`${window.location.origin}/share/${spinnerId}`);
        return spinnerId;
      } else {
        console.error('No spinner ID generated');
        toast.error(t('share.error'));
        return null;
      }
    } catch (error) {
      console.error('Error sharing spinner:', error);
      toast.error(t('share.error'));
      return null;
    } finally {
      setIsSharing(false);
    }
  };

  const requireLogin = (action: () => void) => {
    if (allowAnonymousShare) {
      action();
      return;
    }
    if (!user && !allowAnonymousShare) {
      pendingAction.current = action;
      setShowLoginPrompt(true);
    } else {
      action();
    }
  };

  const handleShareWithoutLogin = () => {
    setShowLoginPrompt(false);
    setAllowAnonymousShare(true);
    if (pendingAction.current) {
      pendingAction.current();
      pendingAction.current = null;
    }
  };

  // Helper function to save current settings
  const saveCurrentSettings = () => {
    setSpinnerName(spinnerName);
    if (sidebarSettings?.participants && Array.isArray(sidebarSettings.participants)) {
      try {
        const participantsWithIds = sidebarSettings.participants.map((name: string, index: number) => ({
          id: `${Date.now()}-${index}`,
          name
        }));
        localStorage.setItem('savedParticipants', JSON.stringify(participantsWithIds));
      } catch (error) {
        console.error('Error caching participants:', error);
      }
    }
  };

  // Named handler for copy
  const handleCopy = async () => {
    if (!user && !allowAnonymousShare) {
      pendingAction.current = handleCopy;
      setShowLoginPrompt(true);
      return;
    }
    const spinnerId = await createOrUpdateShareLink();
    setLastSavedName(spinnerName);
    setNameChanged(false);
    
    // Save current settings
    saveCurrentSettings();
    
    const link = spinnerId ? `${window.location.origin}/share/${spinnerId}` : '';
    if (link) {
      try {
        await navigator.clipboard.writeText(link);
        toast.success(t('share.copied'));
        setShowLinkInput(true);
      } catch (err) {
        // fallback
        const textArea = document.createElement('textarea');
        textArea.value = link;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          toast.success(t('share.copied'));
          setShowLinkInput(true);
        } catch (e) {
          toast.error(t('share.error'));
        }
        document.body.removeChild(textArea);
      }
    }
  };

  const handleWhatsApp = async () => {
    if (!user && !allowAnonymousShare) {
      pendingAction.current = handleWhatsApp;
      setShowLoginPrompt(true);
      return;
    }
    const spinnerId = await createOrUpdateShareLink();
    setLastSavedName(spinnerName);
    setNameChanged(false);
    saveCurrentSettings();
    const link = spinnerId ? `${window.location.origin}/share/${spinnerId}` : '';
    if (link) {
      const shareText = shareTemplate(link, spinnerName);
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`);
    }
  };

  const handleFacebook = async () => {
    if (!user && !allowAnonymousShare) {
      pendingAction.current = handleFacebook;
      setShowLoginPrompt(true);
      return;
    }
    const spinnerId = await createOrUpdateShareLink();
    setLastSavedName(spinnerName);
    setNameChanged(false);
    saveCurrentSettings();
    const link = spinnerId ? `${window.location.origin}/share/${spinnerId}` : '';
    if (link) {
      // Try to open Facebook sharer, fallback to copying link if blocked
      const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`;
      const popup = window.open(facebookUrl, '_blank', 'width=600,height=400');
      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        // Popup was blocked, copy link instead
        try {
          await navigator.clipboard.writeText(link);
          toast.success('Link copied! Paste it in your Facebook post.');
        } catch (err) {
          const textArea = document.createElement('textarea');
          textArea.value = link;
          document.body.appendChild(textArea);
          textArea.select();
          try {
            document.execCommand('copy');
            toast.success('Link copied! Paste it in your Facebook post.');
          } catch (e) {
            toast.error(t('share.error'));
          }
          document.body.removeChild(textArea);
        }
      } else {
        // Facebook opened, also copy the link as backup
        try {
          await navigator.clipboard.writeText(link);
          toast.success('Facebook opened! Link also copied to clipboard.');
        } catch (err) {
          const textArea = document.createElement('textarea');
          textArea.value = link;
          document.body.appendChild(textArea);
          textArea.select();
          try {
            document.execCommand('copy');
            toast.success('Facebook opened! Link also copied to clipboard.');
          } catch (e) {
            toast.error(t('share.error'));
          }
          document.body.removeChild(textArea);
        }
      }
    }
  };

  const handleTwitter = async () => {
    if (!user && !allowAnonymousShare) {
      pendingAction.current = handleTwitter;
      setShowLoginPrompt(true);
      return;
    }
    const spinnerId = await createOrUpdateShareLink();
    setLastSavedName(spinnerName);
    setNameChanged(false);
    saveCurrentSettings();
    const link = spinnerId ? `${window.location.origin}/share/${spinnerId}` : '';
    if (link) {
      // Create share text using translation template
      const shareText = spinnerName 
        ? `${spinnerName} - ${t('share.shareTemplate').replace('{{link}}', '')}`
        : t('share.shareTemplate').replace('{{link}}', '');
      
      // Use only the URL parameter, not both text and URL
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(link)}`);
    }
  };

  const handleInstagram = async () => {
    if (!user && !allowAnonymousShare) {
      pendingAction.current = handleInstagram;
      setShowLoginPrompt(true);
      return;
    }
    const spinnerId = await createOrUpdateShareLink();
    setLastSavedName(spinnerName);
    setNameChanged(false);
    saveCurrentSettings();
    const link = spinnerId ? `${window.location.origin}/share/${spinnerId}` : '';
    if (link) {
      // Copy link to clipboard and open Instagram
      try {
        await navigator.clipboard.writeText(link);
        toast.success('Link copied! Paste it in your Instagram DM or story.');
      } catch (err) {
        const textArea = document.createElement('textarea');
        textArea.value = link;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          toast.success('Link copied! Paste it in your Instagram DM or story.');
        } catch (e) {
          toast.error(t('share.error'));
        }
        document.body.removeChild(textArea);
      }
      // Open Instagram
      window.open('https://www.instagram.com/', '_blank');
    }
  };



  const handleTelegram = async () => {
    if (!user && !allowAnonymousShare) {
      pendingAction.current = handleTelegram;
      setShowLoginPrompt(true);
      return;
    }
    const spinnerId = await createOrUpdateShareLink();
    setLastSavedName(spinnerName);
    setNameChanged(false);
    saveCurrentSettings();
    const link = spinnerId ? `${window.location.origin}/share/${spinnerId}` : '';
    if (link) {
      const shareText = shareTemplate(link, spinnerName);
      window.open(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(shareText)}`);
    }
  };

  const handleTikTok = async () => {
    if (!user && !allowAnonymousShare) {
      pendingAction.current = handleTikTok;
      setShowLoginPrompt(true);
      return;
    }
    const spinnerId = await createOrUpdateShareLink();
    setLastSavedName(spinnerName);
    setNameChanged(false);
    saveCurrentSettings();
    const link = spinnerId ? `${window.location.origin}/share/${spinnerId}` : '';
    if (link) {
      // Copy link to clipboard and open TikTok
      try {
        await navigator.clipboard.writeText(link);
        toast.success('Link copied! Paste it in your TikTok bio or story.');
      } catch (err) {
        const textArea = document.createElement('textarea');
        textArea.value = link;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          toast.success('Link copied! Paste it in your TikTok bio or story.');
        } catch (e) {
          toast.error(t('share.error'));
        }
        document.body.removeChild(textArea);
      }
      // Open TikTok
      window.open('https://www.tiktok.com/', '_blank');
    }
  };

  const shareOptions = [
    { icon: <WhatsappSVG />, label: 'WhatsApp', onClick: handleWhatsApp },
    { icon: <FacebookSVG />, label: 'Facebook', onClick: handleFacebook },
    { icon: <TwitterSVG />, label: 'Twitter', onClick: handleTwitter },
    { icon: <InstagramSVG />, label: 'Instagram', onClick: handleInstagram },
    { icon: <TelegramSVG />, label: 'Telegram', onClick: handleTelegram },
    { icon: <TikTokSVG />, label: 'TikTok', onClick: handleTikTok },
  ];

  if (!isOpen) return null;
  if (!user && !allowAnonymousShare && showLoginPrompt) {
    return (
      <ShareLoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => { setShowLoginPrompt(false); onClose(); }}
        onShareWithoutLogin={handleShareWithoutLogin}
        title={t('share.loginPrompt.title', 'Login to Share your Spinner')}
      />
    );
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={t('share.saveAndShareTitle', 'Save and Share Spinner')}>
        <div className="z-50">
          <div className="space-y-4">
            <div>
              <label className="font-bold text-gray-700 dark:text-gray-300 mb-2 text-left block">
                {t('share.spinnerName', 'Spinner Name')}
              </label>
              <Input
                type="text"
                placeholder={t('share.spinnerNamePlaceholder', 'Enter a name for your spinner (optional)')}
                value={spinnerName}
                maxLength={32}
                onChange={e => setLocalSpinnerName(e.target.value.slice(0, 32))}
                className="w-full"
              />
              <div className="text-xs text-right text-gray-400 mt-1">{spinnerName.length}/32</div>
            </div>
            <div>
              <div className="font-bold text-gray-700 dark:text-gray-300 mb-2 text-left">
                {t('share.shareInSocial', 'Share in Social')}
              </div>
              <div className="flex justify-between items-center mb-2">
                {shareOptions.map(opt => (
                  <button
                    key={opt.label}
                    title={t(`share.${opt.label.toLowerCase()}`, opt.label)}
                    onClick={opt.onClick}
                    className="hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full p-2 transition flex items-center justify-center"
                  >
                    {opt.icon}
                  </button>
                ))}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 text-center mb-2">
                {t('share.instagramTiktokNote')}
              </div>
            </div>
            <div>
              <label className="font-bold text-gray-700 dark:text-gray-300 mb-2 text-left block">
                {t('share.directLink', 'Direct Link')}
              </label>
              <div className="flex items-center gap-2">
                {!showLinkInput && (
                  <Button variant="outline" onClick={handleCopy} style={{ height: 48, width: 48, minWidth: 48, minHeight: 48, fontSize: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }} title={t('share.copyLink')}>
                    <CopyIcon size={28} />
                  </Button>
                )}
                {!showLinkInput && nameChanged && (
                  <span className="text-xs text-yellow-600 ml-2 break-words">{t('share.nameChanged', 'Name updated. Click the copy icon to get the new link.')}</span>
                )}
                {showLinkInput && (
                  <>
                    <Input value={shareUrl} readOnly className="flex-1" />
                    <Button variant="outline" onClick={handleCopy} style={{ height: 48, width: 48, minWidth: 48, minHeight: 48, fontSize: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }} title={t('share.copyLink')}>
                      <CopyIcon size={28} />
                    </Button>
                  </>
                )}
              </div>
            </div>
            <div className="flex gap-2 justify-center mt-4">
              <Button
                onClick={async () => {
                  await createOrUpdateShareLink();
                  saveCurrentSettings();
                  toast.success(t('share.saved', 'Spinner saved!'));
                  onClose();
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {t('share.save', 'Save')}
              </Button>
              <Button onClick={onClose} className="w-auto">
                {t('common.close', 'Close')}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}; 